import { useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { dimensions, DESIGN_WIDTH, DESIGN_HEIGHT } from '../../data/homeDimensions'
import { HOME_MOBILE_MQ, homeMobilePaddingX } from './homeMobileLayout'
import FigmaIconFrame from '../common/FigmaIconFrame'
import scale from '../../theme/scale'

function DimensionCardHeader({ title, description, icon, iconBgColor, educationLayout }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
        width: 360,
        position: educationLayout ? 'absolute' : 'relative',
        left: educationLayout ? 20 : undefined,
        top: educationLayout ? 20 : undefined,
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          width: 52,
          borderRadius: '10px',
          bgcolor: iconBgColor,
          p: '10px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{ width: 32, height: 32, objectFit: 'contain', display: 'block' }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 20,
            lineHeight: 1.3,
            color: '#111',
            mb: '4px',
            fontWeight: 400,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.5,
            color: 'rgba(17,17,17,0.8)',
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  )
}

function DimensionCard({
  title,
  description,
  image,
  icon,
  iconBgColor,
  path,
  imageHeight,
  imageScale,
  educationLayout,
  sx = {},
}) {
  return (
    <Box
      component={RouterLink}
      to={path}
      sx={{
        width: 400,
        height: 420,
        bgcolor: '#fffefa',
        borderRadius: '20px',
        overflow: 'hidden',
        textDecoration: 'none',
        color: '#111',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: educationLayout ? 0 : '20px',
        pt: educationLayout ? 0 : '20px',
        position: 'relative',
        flexShrink: 0,
        ...sx,
      }}
    >
      <DimensionCardHeader
        title={title}
        description={description}
        icon={icon}
        iconBgColor={iconBgColor}
        educationLayout={educationLayout}
      />

      <Box
        sx={{
          width: '100%',
          flex: educationLayout || imageHeight ? 'none' : 1,
          height: educationLayout ? 320 : imageHeight ?? 'auto',
          minHeight: educationLayout ? 320 : imageHeight ? imageHeight : 0,
          borderRadius: educationLayout ? '20px' : '20px',
          overflow: 'hidden',
          position: educationLayout ? 'absolute' : 'relative',
          left: educationLayout ? 0 : undefined,
          top: educationLayout ? 100 : undefined,
          mt: educationLayout ? 0 : undefined,
        }}
      >
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{
            position: 'absolute',
            inset: imageScale ? undefined : 0,
            width: imageScale?.width ?? '100%',
            height: imageScale?.height ?? '100%',
            left: imageScale?.left ?? 0,
            top: imageScale?.top ?? 0,
            maxWidth: 'none',
            objectFit: 'cover',
            display: 'block',
            borderRadius: '20px',
          }}
        />
      </Box>
    </Box>
  )
}

function DimensionsCanvas() {
  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: 'calc(50% - 70px)',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          color: '#fffefa',
          zIndex: 1,
          pointerEvents: 'none',
          width: '100%',
          px: 2,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: scale.fontSize.sectionTitle,
            lineHeight: 1.1,
            fontWeight: 400,
            whiteSpace: 'pre-line',
          }}
        >
          {`The foundation works \nacross\u00a07 key dimensions\u00a0`}
        </Typography>
      </Box>

      {dimensions.map((dim) => {
        const card = (
          <DimensionCard
            title={dim.title}
            description={dim.description}
            image={dim.image}
            icon={dim.icon}
            iconBgColor={dim.iconBgColor}
            path={dim.path}
            imageHeight={dim.imageHeight}
            imageScale={dim.imageScale}
            educationLayout={dim.educationLayout}
          />
        )

        if (dim.rotateOrigin) {
          return (
            <Box
              key={dim.title}
              sx={{
                position: 'absolute',
                left: dim.left,
                top: dim.top,
                zIndex: dim.zIndex,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ transform: `rotate(${dim.rotate}deg)` }}>{card}</Box>
            </Box>
          )
        }

        return (
          <Box
            key={dim.title}
            sx={{
              position: 'absolute',
              left: dim.left,
              top: dim.top,
              zIndex: dim.zIndex,
              transform: dim.rotate ? `rotate(${dim.rotate}deg)` : 'none',
            }}
          >
            {card}
          </Box>
        )
      })}
    </>
  )
}

function ScaledDimensionsLayout() {
  const containerRef = useRef(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return
      const width = containerRef.current.offsetWidth
      setScale(Math.min(width / DESIGN_WIDTH, 1))
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        maxWidth: DESIGN_WIDTH,
        mx: 'auto',
        height: DESIGN_HEIGHT * scale,
        overflow: 'hidden',
        bgcolor: '#111',
      }}
    >
      <Box
        sx={{
          width: DESIGN_WIDTH,
          height: DESIGN_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'relative',
          bgcolor: '#111',
        }}
      >
        <DimensionsCanvas />
      </Box>
    </Box>
  )
}

function MobileDimensionCard({ title, description, image, icon, iconBgColor, path }) {
  return (
    <Box
      component={RouterLink}
      to={path}
      sx={{
        width: '100%',
        bgcolor: '#fffefa',
        borderRadius: '20px',
        overflow: 'hidden',
        textDecoration: 'none',
        color: '#111',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        pt: '20px',
      }}
    >
      <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%', px: '20px' }}>
        <FigmaIconFrame src={icon} bgColor={iconBgColor} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, lineHeight: 'normal', color: '#111', mb: '4px', fontWeight: 400 }}>
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: 1.5,
              color: 'rgba(17,17,17,0.8)',
              fontWeight: 400,
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: '100%', height: { xs: 200, sm: 240 }, overflow: 'hidden', borderRadius: '20px' }}>
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>
    </Box>
  )
}

export default function IndiaMapDimensions() {
  return (
    <Box component="section" sx={{ width: '100%', bgcolor: '#111', overflow: 'hidden' }}>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <ScaledDimensionsLayout />
      </Box>

      <Box
        sx={{
          display: { xs: 'block', lg: 'none' },
          py: { xs: 6, sm: 8 },
          px: homeMobilePaddingX,
          boxSizing: 'border-box',
          [HOME_MOBILE_MQ]: { py: '40px' },
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: 40, sm: 48 },
            textAlign: 'center',
            color: '#fffefa',
            mb: { xs: 5, sm: 5 },
            lineHeight: 'normal',
            fontWeight: 400,
            whiteSpace: 'pre-line',
            maxWidth: 323,
            mx: 'auto',
            [HOME_MOBILE_MQ]: { fontSize: 40, mb: '40px' },
          }}
        >
          {`The foundation works \nacross 7 key dimensions`}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: { xs: '20px', sm: 2.5 },
            maxWidth: { xs: 362, sm: 640 },
            mx: 'auto',
            [HOME_MOBILE_MQ]: {
              gridTemplateColumns: '1fr',
              gap: '20px',
              maxWidth: 362,
            },
          }}
        >
          {dimensions.map((dim) => (
            <MobileDimensionCard key={dim.title} {...dim} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
